import { useState } from 'react';

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPage?: 'acelera' | 'aceleracao';
}

export default function LeadFormModal({ isOpen, onClose, landingPage = 'aceleracao' }: LeadFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Capturar UTMs da URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || ''
      };

      // Obter data e hora no formato Brasil (São Paulo)
      const now = new Date();
      const brasiliaTime = new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(now);

      // Preparar payload - mesma estrutura do formulário principal
      const payload = {
        name: formData.name,
        phone: formData.phone,
        ...utmParams,
        landing_page: landingPage,
        formulario: 'Botão_WPP_Site',
        data_hora_conversao: brasiliaTime
      };

      // Enviar para webhook
      await fetch('https://webhooks.manager01.m4track.com.br/webhook/lp-2026-m4', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      setIsSubmitting(false);
      setShowSuccess(true);

      // Reset após 5 segundos
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ name: '', phone: '' });
        onClose();
      }, 5000);
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ name: '', phone: '' });
        onClose();
      }, 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Formatar campo WhatsApp
    if (name === 'phone') {
      const numbers = value.replace(/\D/g, '');
      let formatted = numbers;
      
      if (numbers.length <= 11) {
        if (numbers.length > 2) {
          formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        }
        if (numbers.length > 7) {
          formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
        }
      }
      
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl max-w-lg w-full">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-gray-600 text-xl"></i>
        </button>

        {showSuccess ? (
          // Success Message - Igual ao formulário principal
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Informações Recebidas!</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Recebemos suas informações com sucesso! Acabamos de enviar uma mensagem no seu WhatsApp com os próximos passos.
            </p>
            <div className="space-y-3">
              <button 
                onClick={onClose}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Verificar WhatsApp
              </button>
              <button 
                onClick={onClose}
                className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        ) : (
          // Form
          <div className="p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-green-600 text-2xl"></i>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Quase lá!
                  </h3>
                  <p className="text-sm text-gray-600">
                    Preencha seus dados para falar no Whatsapp
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome */}
              <div>
                <label htmlFor="nome" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-user-line text-gray-500"></i>
                  Nome completo
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-base"
                  placeholder="Digite seu nome completo"
                />
              </div>

              {/* Telefone */}
              <div>
                <label htmlFor="telefone" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-whatsapp-line text-gray-500"></i>
                  WhatsApp
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-base"
                  placeholder="(11) 99999-9999"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3.5 rounded-xl font-bold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Enviar
                    </>
                  )}
                </button>
              </div>

              <p className="text-center text-xs text-gray-500">
                <i className="ri-lock-line mr-1"></i>
                Seus dados estão seguros e protegidos
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
